import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Terminal, FileCheck, Lock, CreditCard, Landmark, Wallet } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  es: {
    seoTitle: "ELADPENTESTER — Auditoría de Seguridad y Bug Bounty sin Intermediarios",
    seoDesc: "Pentesting web y API, gestión de bug bounty y divulgación responsable ISO 29147. Trato directo con el investigador. España y remoto internacional.",
    coverage: "España & Internacional",
    admin: "Admin",
    contactar: "Contactar",
    heroBadge: "Lead Security Researcher // Bug Bounty Hub",
    heroL1: "Auditoría de seguridad",
    heroL2: "y bug bounty",
    heroL3: "sin intermediarios.",
    heroDiff: "Informes claros alineados con ISO 29147 y divulgación responsable. Experiencia real en bug bounty y trato directo.",
    ctaQuote: "Pedir Presupuesto",
    ctaServices: "Servicios",
    marquee: ["// BOUNTYFLOW HUB", "PENTESTING OFENSIVO", "NORMA ISO 29147", "DIVULGACIÓN RESPONSABLE", "REPORTE SIN INTERMEDIARIOS"],
    servicesTag: "Servicios Principales",
    servicesTitle: "Soluciones de Auditoría",
    services: [
      { id: "web", tag: "AUDIT: WEB & API", tagColor: "text-[#f0abfc]", title: "Pentesting Web & API", desc: "Evaluación profunda contra ataques lógicos, inyecciones y fallos de autenticación crítica.", foot: "Informes Ejecutivos", img: "https://images.pexels.com/photos/5380589/pexels-photo-5380589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", bg: "radial-gradient(100% 100% at 20% 20%,#e879f933,transparent),linear-gradient(160deg,#0d0914,#3b0764)" },
      { id: "bounty", tag: "AUDIT: BOUNTY_MANAGEMENT", tagColor: "text-[#38bdf8]", title: "Gestión de Bug Bounty", desc: "Triaje riguroso, mediación con hackers y protección de presupuesto frente a informes falsos.", foot: "Trato y Mediación", img: "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXRhJTIwZ3JhcGglMjBjeWJlciUyMHNlY3VyaXR5JTIwcHVycGxlfGVufDB8fHx8MTc5MjIwMDE3NXww&ixlib=rb-4.1.0&q=85", bg: "radial-gradient(100% 100% at 80% 20%,#38bdf833,transparent),linear-gradient(160deg,#0d0914,#1e1b4b)" },
      { id: "iso", tag: "AUDIT: ISO_STANDARDS", tagColor: "text-[#d8b4fe]", title: "Norma ISO 29147", desc: "Estructura formal para divulgación de vulnerabilidades y cumplimiento con regulaciones.", foot: "Cumplimiento Global", img: "https://images.pexels.com/photos/38117127/pexels-photo-38117127.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", bg: "radial-gradient(100% 100% at 50% 50%,#a855f733,transparent),linear-gradient(160deg,#0d0914,#2e1065)" },
    ],
    priceNote: "Presupuesto a medida",
    trust: [
      { id: "direct", title: "TRATO DIRECTO", desc: "Sin comerciales. Hablas directamente con Abdelhamid Eladraoui." },
      { id: "iso", title: "ISO 29147", desc: "Divulgación responsable y metodologías reconocidas." },
      { id: "coverage", title: "ESPAÑA & GLOBAL", desc: "Servicios remotos e in-situ para startups y empresas." },
    ],
    payTag: "Pagos Flexibles",
    payTitle: "Métodos de Pago Aceptados",
    payments: [
      { id: "paypal", title: "PAYPAL", desc: "Pago seguro online con protección al comprador." },
      { id: "visa", title: "VISA", desc: "Tarjeta de crédito o débito, cobro tras aceptar el presupuesto." },
      { id: "bank", title: "TRANSFERENCIA", desc: "Transferencia bancaria con factura formal para empresas." },
    ],
    validTag: "Confidencialidad",
    validTitle: "Validación de Clientes",
    validText: "Por acuerdos estrictos de confidencialidad, las referencias técnicas se facilitan a petición directa.",
    validFoot: "Auditorías Protegidas bajo NDA",
    formTag: "Formulario Corto",
    formTitle: "Solicitar Presupuesto",
    formDesc: "Recibe una estimación técnica directa y confidencial para tu infraestructura.",
    labelName: "Nombre o Empresa",
    phName: "Tu nombre / Empresa",
    labelEmail: "Email Profesional",
    phEmail: "email@empresa.com",
    labelService: "Servicio Requerido",
    serviceOptions: ["Pentesting Web / API", "Gestión Bug Bounty", "Auditoría ISO 29147", "Auditoría Integral"],
    labelMessage: "Detalles del Alcance",
    phMessage: "Describe brevemente tus sistemas a auditar...",
    submit: "Enviar Petición de Contacto",
    sending: "Enviando...",
    toastOk: "Petición enviada. Recibirás respuesta directa y confidencial.",
    toastErr: "No se pudo enviar. Revisa los campos e inténtalo de nuevo.",
    footerLegal: "ELADRAOUI Tech & Cyber Security Solutions",
    footerGeo: "España · Remoto Internacional",
  },
  en: {
    seoTitle: "ELADPENTESTER — Security Audits & Bug Bounty, No Middlemen",
    seoDesc: "Web & API pentesting, bug bounty management and ISO 29147 responsible disclosure. Direct line to the researcher. Spain & remote worldwide.",
    coverage: "Spain & International",
    admin: "Admin",
    contactar: "Contact",
    heroBadge: "Lead Security Researcher // Bug Bounty Hub",
    heroL1: "Security auditing",
    heroL2: "and bug bounty",
    heroL3: "with no middlemen.",
    heroDiff: "Clear reports aligned with ISO 29147 and responsible disclosure. Real bug bounty experience and direct dealing.",
    ctaQuote: "Request a Quote",
    ctaServices: "Services",
    marquee: ["// BOUNTYFLOW HUB", "OFFENSIVE PENTESTING", "ISO 29147 STANDARD", "RESPONSIBLE DISCLOSURE", "DIRECT REPORTING"],
    servicesTag: "Core Services",
    servicesTitle: "Audit Solutions",
    services: [
      { id: "web", tag: "AUDIT: WEB & API", tagColor: "text-[#f0abfc]", title: "Web & API Pentesting", desc: "Deep assessment against logic attacks, injections and critical authentication flaws.", foot: "Executive Reports", img: "https://images.pexels.com/photos/5380589/pexels-photo-5380589.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", bg: "radial-gradient(100% 100% at 20% 20%,#e879f933,transparent),linear-gradient(160deg,#0d0914,#3b0764)" },
      { id: "bounty", tag: "AUDIT: BOUNTY_MANAGEMENT", tagColor: "text-[#38bdf8]", title: "Bug Bounty Management", desc: "Rigorous triage, mediation with hackers and budget protection against false reports.", foot: "Direct Mediation", img: "https://images.unsplash.com/photo-1751448555253-f39c06e29d82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXRhJTIwZ3JhcGglMjBjeWJlciUyMHNlY3VyaXR5JTIwcHVycGxlfGVufDB8fHx8MTc5MjIwMDE3NXww&ixlib=rb-4.1.0&q=85", bg: "radial-gradient(100% 100% at 80% 20%,#38bdf833,transparent),linear-gradient(160deg,#0d0914,#1e1b4b)" },
      { id: "iso", tag: "AUDIT: ISO_STANDARDS", tagColor: "text-[#d8b4fe]", title: "ISO 29147 Standard", desc: "Formal structure for vulnerability disclosure and regulatory compliance.", foot: "Global Compliance", img: "https://images.pexels.com/photos/38117127/pexels-photo-38117127.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", bg: "radial-gradient(100% 100% at 50% 50%,#a855f733,transparent),linear-gradient(160deg,#0d0914,#2e1065)" },
    ],
    priceNote: "Custom quote",
    trust: [
      { id: "direct", title: "DIRECT DEALING", desc: "No sales reps. You talk directly with Abdelhamid Eladraoui." },
      { id: "iso", title: "ISO 29147", desc: "Responsible disclosure and recognised methodologies." },
      { id: "coverage", title: "SPAIN & GLOBAL", desc: "Remote and on-site services for startups and enterprises." },
    ],
    payTag: "Flexible Payments",
    payTitle: "Accepted Payment Methods",
    payments: [
      { id: "paypal", title: "PAYPAL", desc: "Secure online payment with buyer protection." },
      { id: "visa", title: "VISA", desc: "Credit or debit card, charged after quote approval." },
      { id: "bank", title: "BANK TRANSFER", desc: "Bank transfer with formal invoicing for companies." },
    ],
    validTag: "Confidentiality",
    validTitle: "Client Validation",
    validText: "Due to strict confidentiality agreements, technical references are provided on direct request.",
    validFoot: "Audits Protected under NDA",
    formTag: "Short Form",
    formTitle: "Request a Quote",
    formDesc: "Get a direct, confidential technical estimate for your infrastructure.",
    labelName: "Name or Company",
    phName: "Your name / Company",
    labelEmail: "Work Email",
    phEmail: "email@company.com",
    labelService: "Service Required",
    serviceOptions: ["Web / API Pentesting", "Bug Bounty Management", "ISO 29147 Audit", "Full Audit"],
    labelMessage: "Scope Details",
    phMessage: "Briefly describe the systems to audit...",
    submit: "Send Contact Request",
    sending: "Sending...",
    toastOk: "Request sent. You will get a direct, confidential reply.",
    toastErr: "Could not send. Check the fields and try again.",
    footerLegal: "ELADRAOUI Tech & Cyber Security Solutions",
    footerGeo: "Spain · Remote Worldwide",
  },
};

const TRUST_ICONS = { direct: Terminal, iso: FileCheck, coverage: Shield };
const PAY_ICONS = { paypal: Wallet, visa: CreditCard, bank: Landmark };
const PAY_COLORS = { paypal: "text-[#38bdf8]", visa: "text-[#e879f9]", bank: "text-[#a855f7]" };

export default function LandingPage({ lang = "es" }) {
  const t = COPY[lang] || COPY.es;
  const otherLang = lang === "es" ? "en" : "es";
  const [form, setForm] = useState({ name: "", email: "", service: t.serviceOptions[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success(t.toastOk);
      setForm({ name: "", email: "", service: t.serviceOptions[0], message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#0d0914] text-[#f5f3ff] min-h-screen f-text antialiased" data-testid="landing-page" lang={lang}>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path={lang === "es" ? "/" : "/en"}
        lang={lang}
      />
      {/* NAV */}
      <nav className="border-b border-[#2e1065] bg-[#0d0914]/90 backdrop-blur sticky top-0 z-40 px-6 py-4" data-testid="main-nav">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-3" data-testid="brand-link">
            <img src="/assets/logo.jpg" alt="ELADPENTESTER logo" className="w-9 h-9 rounded-lg object-cover border border-[#581c87]" data-testid="brand-logo-img" />
            <div>
              <span className="f-poster text-lg text-white uppercase tracking-wide block leading-none" data-content-slot="brand-logo" data-slot-source="brief">ELADPENTESTER</span>
              <span className="text-[9px] f-mono text-[#d8b4fe] tracking-widest uppercase" data-content-slot="sub-lead" data-slot-source="brief">ELADRAOUI Abdelhamid</span>
            </div>
          </a>
          <div className="flex items-center gap-4">
            <span className="f-mono text-xs text-[#a855f7] hidden md:inline" data-content-slot="coverage-text" data-slot-source="brief">{t.coverage}</span>
            <Link to={otherLang === "en" ? "/en" : "/"} className="f-mono text-[10px] uppercase tracking-widest border border-[#581c87] rounded px-2 py-1 text-[#d8b4fe] hover:border-[#e879f9] transition-colors" data-testid="lang-switch">
              {otherLang.toUpperCase()}
            </Link>
            <Link to="/admin/login" className="f-mono text-[10px] uppercase tracking-widest text-[#d8b4fe] hover:text-[#e879f9] transition-colors hidden sm:inline" data-testid="admin-access-link">
              <Lock className="inline w-3 h-3 mr-1 -mt-0.5" />{t.admin}
            </Link>
            <a href="#contacto" className="f-poster uppercase text-sm tracking-wide bg-[#e879f9] text-[#0d0914] px-4 py-2 rounded hover:bg-white transition-colors" data-testid="nav-contact-btn">{t.contactar}</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header id="hero" className="py-24 px-6 border-b border-[#2e1065] relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-6xl mx-auto">
          <div className="inline-block text-xs f-mono uppercase tracking-widest text-[#e879f9] bg-[#2e1065]/60 border border-[#581c87] px-3 py-1 rounded mb-6" data-testid="hero-badge">
            {t.heroBadge}
          </div>
          <h1 className="f-poster text-5xl sm:text-7xl md:text-8xl text-white uppercase leading-[0.92] mb-8" data-testid="hero-heading">
            <span className="ln"><span>{t.heroL1}</span></span>
            <span className="ln"><span>{t.heroL2}</span></span>
            <span className="ln"><span className="text-[#e879f9]">{t.heroL3}</span></span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-6">
              <p className="text-base text-[#d8b4fe] max-w-lg mb-8 leading-relaxed" data-content-slot="hero-diff" data-slot-source="brief" data-testid="hero-diff">
                {t.heroDiff}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#contacto" className="f-poster text-base uppercase tracking-wide bg-[#e879f9] text-[#0d0914] px-8 py-4 rounded hover:bg-white transition-colors shadow-lg shadow-[#e879f9]/20" data-testid="hero-quote-btn">
                  {t.ctaQuote}
                </a>
                <a href="#soluciones" className="f-poster text-base uppercase tracking-wide border border-[#581c87] text-white px-8 py-4 rounded hover:border-[#e879f9] transition-colors" data-testid="hero-services-btn">
                  {t.ctaServices}
                </a>
              </div>
            </div>

            <div className="lg:col-span-6 hero-strip">
              <div className="aspect-[16/9] rounded-2xl border border-[#581c87] p-3 bg-[#170e28] shadow-2xl relative overflow-hidden" data-testid="hero-mockup">
                <div className="h-full w-full rounded-xl overflow-hidden drift-box relative"
                     style={{ backgroundImage: "url(/assets/dashboard.jpg)", backgroundSize: "135% 135%", backgroundPosition: "10% 10%" }}>
                  <div className="absolute inset-0 bg-[#0d0914]/30" />
                  <div className="relative p-5 h-full flex flex-col justify-between f-mono text-xs">
                    <div className="flex justify-between text-[#e879f9]">
                      <span>CYBER_RADAR_v2</span>
                      <span className="text-[#38bdf8]">THREATS_ISOLATED</span>
                    </div>
                    <div className="text-center font-bold text-lg text-white drop-shadow">ISO 29147 DISCLOSURE</div>
                    <div className="flex justify-between text-[11px] text-[#d8b4fe]">
                      <span>PENTEST: OWASP 2024</span>
                      <span className="text-emerald-400">VERIFIED</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MARQUEE */}
      <div className="border-b border-[#2e1065] bg-[#150a24] py-3 overflow-hidden" data-testid="marquee-strip">
        <div className="mq text-xs f-mono uppercase tracking-widest text-[#d8b4fe] flex gap-10 items-center">
          {[...t.marquee, ...t.marquee].map((item, i) => (
            <span key={i} className="flex gap-10 items-center">
              <span>{item}</span>
              <span className="text-[#e879f9]">•</span>
            </span>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section id="soluciones" className="py-24 px-6 border-b border-[#2e1065]" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14 reveal">
            <span className="text-xs f-mono uppercase tracking-widest text-[#e879f9]">{t.servicesTag}</span>
            <h2 className="f-poster text-4xl sm:text-5xl uppercase text-white mt-1">{t.servicesTitle}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.services.map((s) => (
              <div key={s.id} className="border border-[#3b126d] bg-[#140b22] p-6 rounded-2xl flex flex-col justify-between hover:border-[#e879f9]/60 transition-colors" data-testid={`service-card-${s.id}`}>
                <div className="aspect-video rounded-xl mb-5 overflow-hidden drift-box relative" style={{ backgroundImage: `url(${s.img})`, backgroundSize: "135% 135%" }}>
                  <div className="absolute inset-0" style={{ background: s.bg, opacity: 0.55 }} />
                  <div className={`relative p-3 text-[10px] f-mono ${s.tagColor}`}>{s.tag}</div>
                </div>
                <div>
                  <h3 className="f-poster text-xl uppercase text-white mb-2" data-content-slot={`sol-${s.id}`} data-slot-source="placeholder">{s.title}</h3>
                  <p className="text-xs text-[#d8b4fe] leading-relaxed mb-4">{s.desc}</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="f-mono text-xs text-[#e879f9] font-bold">{s.foot}</span>
                  <span className="f-mono text-[10px] uppercase tracking-widest text-[#38bdf8]">{t.priceNote}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-20 px-6 border-b border-[#2e1065] bg-[#09050e]" data-testid="trust-section">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {t.trust.map((c) => {
            const Icon = TRUST_ICONS[c.id];
            return (
              <div key={c.id} className="border border-[#3b126d] p-6 rounded-xl bg-[#120a1f]" data-testid={`trust-card-${c.id}`}>
                <Icon className="w-6 h-6 mx-auto mb-3 text-[#e879f9]" />
                <div className="f-poster text-3xl text-[#e879f9] mb-1">{c.title}</div>
                <p className="text-xs text-[#d8b4fe]">{c.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PAYMENT METHODS */}
      <section className="py-20 px-6 border-b border-[#2e1065] bg-[#09050e]" data-testid="payment-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs f-mono uppercase tracking-widest text-[#e879f9]">{t.payTag}</span>
            <h2 className="f-poster text-4xl uppercase text-white mt-1">{t.payTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {t.payments.map((p) => {
              const Icon = PAY_ICONS[p.id];
              return (
                <div key={p.id} className="border border-[#3b126d] p-6 rounded-xl bg-[#120a1f] hover:border-[#e879f9]/60 transition-colors" data-testid={`payment-${p.id}`}>
                  <Icon className={`w-6 h-6 mx-auto mb-3 ${PAY_COLORS[p.id]}`} />
                  <div className="f-poster text-2xl text-white mb-1">{p.title}</div>
                  <p className="text-xs text-[#d8b4fe]">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NDA / VALIDATION */}
      <section className="py-24 px-6 border-b border-[#2e1065]" data-testid="validation-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs f-mono uppercase tracking-widest text-[#e879f9]">{t.validTag}</span>
            <h2 className="f-poster text-4xl uppercase text-white mt-1">{t.validTitle}</h2>
          </div>
          <div className="border border-dashed border-[#581c87] rounded-2xl p-10 text-center bg-[#170e28]/50" data-content-slot="reviews-panel" data-slot-source="placeholder" data-testid="reviews-panel">
            <p className="text-sm text-[#d8b4fe] max-w-md mx-auto">{t.validText}</p>
            <span className="text-[10px] f-mono text-[#a855f7] mt-3 block uppercase">{t.validFoot}</span>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="py-24 px-6 bg-[#09050e]" data-testid="contact-section">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <span className="text-xs f-mono uppercase tracking-widest text-[#e879f9]">{t.formTag}</span>
            <h2 className="f-poster text-4xl uppercase text-white mt-1 mb-3">{t.formTitle}</h2>
            <p className="text-xs text-[#d8b4fe] leading-relaxed mb-6">{t.formDesc}</p>
            <div className="aspect-[4/3] rounded-xl border border-[#3b126d] overflow-hidden drift-box relative"
                 style={{ backgroundImage: "url(/assets/logo.jpg)", backgroundSize: "135% 135%" }}
                 data-testid="contact-shield-box">
              <div className="absolute inset-0 bg-[#0d0914]/40" />
              <div className="relative p-4 f-mono text-[10px] text-[#e879f9]">
                <span>DIRECT_RESEARCHER_LINE</span>
                <div className="mt-8 text-white font-bold drop-shadow">RESPONSIBLE DISCLOSURE</div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 bg-[#140b22] border border-[#3b126d] p-8 rounded-2xl shadow-xl">
            <form className="space-y-4" onSubmit={submit} data-content-slot="contact-form-box" data-slot-source="placeholder" data-testid="contact-form">
              <div>
                <label htmlFor="lead-name" className="block text-xs f-mono uppercase text-[#d8b4fe] mb-1">{t.labelName}</label>
                <input id="lead-name" type="text" required minLength={2} placeholder={t.phName} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#0d0914] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#e879f9] transition-colors"
                  data-testid="contact-name-input" />
              </div>
              <div>
                <label htmlFor="lead-email" className="block text-xs f-mono uppercase text-[#d8b4fe] mb-1">{t.labelEmail}</label>
                <input id="lead-email" type="email" required placeholder={t.phEmail} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#0d0914] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#e879f9] transition-colors"
                  data-testid="contact-email-input" />
              </div>
              <div>
                <label htmlFor="lead-service" className="block text-xs f-mono uppercase text-[#d8b4fe] mb-1">{t.labelService}</label>
                <select id="lead-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
                  className="w-full bg-[#0d0914] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#e879f9] transition-colors"
                  data-testid="contact-service-select">
                  {t.serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="lead-message" className="block text-xs f-mono uppercase text-[#d8b4fe] mb-1">{t.labelMessage}</label>
                <textarea id="lead-message" rows={3} required minLength={5} placeholder={t.phMessage} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-[#0d0914] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#e879f9] transition-colors"
                  data-testid="contact-message-input" />
              </div>
              <button type="submit" disabled={sending}
                className="w-full bg-[#e879f9] text-[#0d0914] f-poster text-base uppercase tracking-wide py-3.5 rounded hover:bg-white transition-colors disabled:opacity-50"
                data-testid="contact-submit-btn">
                {sending ? t.sending : t.submit}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 px-6 border-t border-[#2e1065] bg-[#06030a] text-xs text-[#a855f7]" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="f-poster uppercase text-white tracking-wide" data-content-slot="legal-footer" data-slot-source="brief">
            {t.footerLegal}
          </div>
          <div className="f-mono text-[11px] text-[#d8b4fe]">{t.footerGeo}</div>
        </div>
      </footer>
    </div>
  );
}
